import { useState, useEffect, useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import TopNavBar from '../components/navigation/TopNavBar'
import RoundedButton from '../components/buttons/RoundedButton'
import RoundedButtonLink from '../components/buttons/RoundedButtonLink'
import FormInput from '../components/inputs/FormInput'
import LoadingModal from '../components/modals/LoadingModal'
import * as Constants from '../utils/constants'
import { UserContext } from '../App'
import { isExpired } from 'react-jwt'
import { ToastContainer, toast } from 'react-toastify'
import arrow from '../assets/arrow.png'
import strings from '../res/strings'
import 'react-toastify/dist/ReactToastify.css'

const EditClient = () => {
    const { id } = useParams()

    const [email, setEmail] = useState('')
    const [emailError, setEmailError] = useState(null)

    const [name, setName] = useState('')
    const [nameError, setNameError] = useState(null)

    const [nif, setNif] = useState('')
    const [nifError, setNifError] = useState(null)

    const [address, setAddress] = useState('')
    const [addressError, setAddressError] = useState(null)

    const [zipCode, setZipCode] = useState('')
    const [zipCodeError, setZipCodeError] = useState(null)

    const [locality, setLocality] = useState('')
    const [localityError, setLocalityError] = useState(null)

    const [isLoading, setIsLoading] = useState(true)

    const { loggedUser, setLoggedUser } = useContext(UserContext)
    const navigate = useNavigate()

    useEffect(() => {
        if(loggedUser !== null && loggedUser !== 'refresh') {
            const isTokenExpired = isExpired(loggedUser.jwtToken)

            if(isTokenExpired) {
                setLoggedUser(null)
                navigate('/')
            } else if(loggedUser.role !== Constants.Roles.ADMINIT) {
                navigate('/')
            } else {
                getUserDetails()
            }
        } else if (loggedUser === null) {
            navigate('/')
        }
    }, [loggedUser])

    // Handle get user details
    const getUserDetails = () => {
        fetch(`/api/Backoffice/${id}/Details`, {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()

                    setEmail(jsonRes.email)
                    setName(jsonRes.name)
                    setNif(jsonRes.nif.toString())
                    setAddress(jsonRes.address)
                    setZipCode(jsonRes.postalCode)
                    setLocality(jsonRes.locality)
                } else {
                    showErrorToast(strings.getUserDetailsError)
                    resetVariables()
                }
                
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                resetVariables()
            }, 1000)
        })
    }

    // Handle changes in email input text
    const onChangeEmail = (emailText) => {
        setEmailError(null)
        setEmail(emailText.target.value)
    }

    // Handle changes in name input text
    const onChangeName = (nameText) => {
        setNameError(null)
        setName(nameText.target.value)
    }

    // Handle changes in NIF input text
    const onChangeNIF = (nifText) => {
        setNifError(null)
        setNif(nifText.target.value)
    }

    // Handle changes in address input text
    const onChangeAddress = (addressText) => {
        setAddressError(null)
        setAddress(addressText.target.value)
    }

    // Handle changes in zip code input text
    const onChangeZipCode = (zipCodeText) => {
        setZipCodeError(null)
        setZipCode(zipCodeText.target.value)
    }

    // Handle changes in locality input text
    const onChangeLocality = (localityText) => {
        setLocalityError(null)
        setLocality(localityText.target.value)
    }

    // Check if email is valid
    const isValidEmail = (email) => {
        const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
        return emailPattern.test(email)
    }

    // Check if NIF is valid
    const isValidNIF = (nif) => {
        return /^\d+$/.test(nif)
    }

    // Check if zip code is valid
    const isValidZipCode = (zipCode) => {
        const zipCodePattern = /^\d{4}-\d{3}$/
        return zipCodePattern.test(zipCode)
    }

    // Update user client and navigate back to Users list
    const updateClient = () => {
        let hasErrors = false

        if (email.trim().length === 0) {
            setEmailError(strings.insertEmailError)
            hasErrors = true
        } else if (!isValidEmail(email)) {
            hasErrors = true
            setEmailError(strings.invalidEmail)
        }
    
        if (name.trim().length === 0) {
            hasErrors = true
            setNameError(strings.insertNameError)
        }
    
        if (nif.trim().length === 0) {
            hasErrors = true
            setNifError(strings.insertNifError)
        } else if (!isValidNIF(nif)) {
            hasErrors = true
            setNifError(strings.invalidNif)
        } else if (nif.trim().length < 9) {
            hasErrors = true
            setNifError(strings.invalidNif)
        }
    
        if (address.trim().length === 0) {
            hasErrors = true
            setAddressError(strings.insertAddressError)
        }
    
        if (zipCode.trim().length === 0) {
            hasErrors = true
            setZipCodeError(strings.insertZipCodeError);
        } else if (!isValidZipCode(zipCode)) {
            hasErrors = true
            setZipCodeError(strings.invalidZipCode)
        }
    
        if (locality.trim().length === 0) {
            hasErrors = true
            setLocalityError(strings.insertLocalityError)
        }

        if (!hasErrors) {
            setIsLoading(true)

            const data = { 
                Email: email,
                Name: name,
                NIF: nif,
                Address: address,
                PostalCode: zipCode,
                Locality: locality
            }

            // Update user client
            fetch(`/api/Backoffice/${id}/EditClient`, {
                method:'PUT',
                headers: { 
                  'Accept': 'application/json',
                  'Content-Type': 'application/json; charset=UTF-8',
                  'Authorization': 'Bearer ' + loggedUser.jwtToken
                }, 
                mode: 'cors',
                body: JSON.stringify(data)
            }).then(async response => {
                setTimeout(() => {
                    setIsLoading(false)

                    if (response.status === 200) {
                        navigate(-1)
                    } else {
                        showErrorToast(strings.updateUserError)
                    }
                }, 1000) 
        
            }).catch((error) => {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(error)
                }, 1000) 
            })
        }
    }

    // Resets local state variables
    const resetVariables = () => {
        setEmail('')
        setName('')
        setNif('')
        setAddress('')
        setZipCode('')
        setLocality('')
    }

    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }

    return (
        <>
            <TopNavBar />

            <div className='flex flex-col px-[12.5rem] min-h-screen mb-6'>

                { isLoading ?
                    <LoadingModal />
                :   <>
                        <div className='flex flex-row items-center mt-16 mb-12 space-x-2'>
                            <button onClick={() => navigate(-1)}>
                                <img className='h-5 w-5' src={arrow} alt='Voltar atrás'/>
                            </button>
                            <p className='font-montserrat font-semibold text-accent'>{strings.updateUser} / {strings.client}</p>
                        </div>

                        <p className='font-montserrat text-xl font-bold text-navy'>{strings.info}</p>

                        <div className='flex flex-row mt-4 space-x-6'>
                            <div className='flex flex-1 flex-col'>
                                <label htmlFor='email' className='font-montserrat font-semibold text-sm'>{strings.accessEmail}</label>
                                <FormInput 
                                    id='email' 
                                    type='email'
                                    value={email}
                                    placeholder={strings.insertAccessEmail}
                                    onChange={onChangeEmail}
                                    error={emailError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{emailError ? emailError : ''}</p>
                            </div>

                            <div className='flex flex-1 flex-col'>
                                <label htmlFor='name' className='font-montserrat font-semibold text-sm'>{strings.name}</label>
                                <FormInput 
                                    id='name' 
                                    type='text'
                                    value={name}
                                    placeholder={strings.insertName}
                                    onChange={onChangeName}
                                    error={nameError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{nameError ? nameError : ''}</p>
                            </div>
                        </div>

                        <div className='flex flex-row mt-9 space-x-6'>
                            <div className='flex flex-1 flex-col'>
                                <label htmlFor='nif' className='font-montserrat font-semibold text-sm'>{strings.nif}</label>
                                <FormInput 
                                    id='nif' 
                                    type='text'
                                    value={nif}
                                    placeholder={strings.insertNif}
                                    onChange={onChangeNIF}
                                    error={nifError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{nifError ? nifError : ''}</p>
                            </div>

                            <div className='flex flex-1 flex-col'>
                                <label htmlFor='address' className='font-montserrat font-semibold text-sm'>{strings.address}</label>
                                <FormInput 
                                    id='address' 
                                    type='text'
                                    value={address}
                                    placeholder={strings.insertAddress}
                                    onChange={onChangeAddress}
                                    error={addressError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{addressError ? addressError : ''}</p>
                            </div>
                        </div>

                        <div className='flex flex-row mt-9 space-x-6'>
                            <div className='flex flex-1 flex-col'>
                                <label htmlFor='zipCode' className='font-montserrat font-semibold text-sm'>{strings.zipCode}</label>
                                <FormInput 
                                    id='zipCode' 
                                    type='text'
                                    value={zipCode}
                                    placeholder={strings.insertZipCode}
                                    onChange={onChangeZipCode}
                                    error={zipCodeError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{zipCodeError ? zipCodeError : ''}</p>
                            </div>

                            <div className='flex flex-1 flex-col'>
                                <label htmlFor='locality' className='font-montserrat font-semibold text-sm'>{strings.locality}</label>
                                <FormInput 
                                    id='locality' 
                                    type='text'
                                    value={locality}
                                    placeholder={strings.insertLocality}
                                    onChange={onChangeLocality}
                                    error={localityError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{localityError ? localityError : ''}</p>
                            </div>
                        </div>

                        <div className='flex flex-row items-center mt-10 space-x-6'>
                            <div className='flex w-60'>
                                <RoundedButtonLink 
                                    path='/backoffice-users'
                                    text={strings.cancel}
                                    height={48}
                                    bgColor='#E85F5C'
                                    flex={true}
                                />
                            </div>

                            <div className='flex w-60'>
                                <RoundedButton 
                                    onClick={updateClient}
                                    text={strings.save}
                                    height={48}
                                    bgColor='#007EB4'
                                />
                            </div>
                        
                        </div>
                    </>
                }

            </div>

            <ToastContainer />
        </>
    )
}

export default EditClient